import React, { CSSProperties, ReactNode } from 'react';

/**
 * Detecta se uma string é uma URL válida
 */
const isUrl = (str: string): boolean => {
  return (
    str.startsWith('http://') ||
    str.startsWith('https://') ||
    str.startsWith('data:image') ||
    str.startsWith('//')
  );
};

/**
 * Detecta se uma string é um emoji ou texto simples
 */
const isEmojiOrText = (str: string): boolean => {
  return typeof str === 'string' && !isUrl(str) && str.length <= 50;
};

/**
 * Ícone SVG padrão (chat bubble)
 */
const defaultChatIcon = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Renderiza um ícone baseado no tipo de entrada
 *
 * @param icon - Ícone (ReactNode, URL, emoji ou base64)
 * @param style - Estilos CSS opcionais
 * @param showDefault - Se true, mostra ícone padrão quando icon for undefined
 * @returns Elemento React renderizado
 */
export const renderIcon = (
  icon?: ReactNode | string,
  style?: CSSProperties,
  showDefault: boolean = false
): ReactNode => {
  // Se não há ícone e showDefault é false, retorna null
  if (!icon && !showDefault) {
    return null;
  }

  // Se não há ícone mas showDefault é true, retorna o ícone padrão
  if (!icon && showDefault) {
    return <span style={style}>{defaultChatIcon}</span>;
  }

  // Se é ReactNode, retorna direto (já é um elemento React)
  if (React.isValidElement(icon)) {
    return <span style={style}>{icon}</span>;
  }

  // Se é string
  if (typeof icon === 'string') {
    // Detecta URL (imagem)
    if (isUrl(icon)) {
      return (
        <img
          src={icon}
          alt="Chat icon"
          style={{
            display: 'block',
            objectFit: 'contain',
            ...style,
          }}
        />
      );
    }

    // Detecta emoji ou texto
    if (isEmojiOrText(icon)) {
      return <span style={style}>{icon}</span>;
    }
  }

  // Fallback: retorna o ícone padrão
  if (showDefault) {
    return <span style={style}>{defaultChatIcon}</span>;
  }

  return null;
};

